import { useState, useEffect } from 'react'
import { Box, Typography, CircularProgress, Grid, Card } from '@mui/material'
import { sendGet, sendPost, sendPostFormData } from 'hooks/use-fetch'
import { ChildEdit } from 'Pages/CareNetworkManagement/CareNetworkDetail/ChildEdit'
import { ErrorProvider } from 'ContextProviders/ErrorProvider'
import { LocationCardList } from 'Components/Cards/MultiCardList/LocationCardList'
import { type ChildEditValues, type postError, type ChildSiblings, ChildAuditAccessTypeEnum, AccountType } from 'core/types'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { toast } from 'react-toastify'
import { NotAllowed } from 'Routes/ProtectedRoute'
import { ChildMerge } from 'Pages/CareNetworkManagement/CareNetworkDetail/ChildMerge'
import { generatePath, useNavigate } from 'react-router'
import { InterestFormCard } from 'Components/Cards/InterestFormCard'
import { CareManagementSummary } from 'Components/CareManagement/CareManagementSummary'
import { useRefresh, useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import { ChildInfoCard } from 'Components/ChildDetails/ChildInfoCard'
import * as AppRoutes from 'Pages/Routes'
import CareNetworkDetails from 'Components/CareNetwork/CareNetworkDetails'
import { IncomeCard } from 'Components/Program/IncomeCard'
import { usePermissions } from 'hooks/use-permissions'

export interface ChildDetailsProps {
  childID: number
}
interface intakeVisiblityUpdate {
  childId: number
  visible: boolean
}

export function ChildDetails (props: ChildDetailsProps): JSX.Element {
  const account = useAccountId()
  const userPermissions = usePermissions()
  const [loaded, setLoaded] = useState(false)
  const [isRequestLoading, setIsRequestLoading] = useState(false)
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const [child, setChild] = useState<ChildEditValues>({} as ChildEditValues)
  const [isUnborn, setIsUnborn] = useState(false)
  const [open, setOpen] = useState(false)
  const refreshTime = useRefresh()
  const disp = useRefreshDispatch()
  const setRefreshTime = (): void => { disp({ type: 'refresh' }) }
  const [unauthorized, setUnauthorized] = useState(false)
  const [showConfirmMerge, setShowConfirmMerge] = useState(false)
  const nav = useNavigate()
  const [showCareManagement, setShowCareManagement] = useState(false)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      let url = `/ChildDetails/EditableRecord?childId=${props.childID}`
      if (account?.type === AccountType.ISD) {
        url += `&isdId=${account.id}`
      }
      const { response, success, status } = await sendGet(url, {})
      const { response: rsp2 } = await (sendGet('/ChildDetails/CanShowCareManagement', {}))
      setShowCareManagement(rsp2)
      if (success) {
        const details = response.response
        const child = { ...details, image: details.imageKey ?? '' }
        setChild(child)
        setIsUnborn(child.isUnborn)
      } else {
        if (status === 401) {
          setUnauthorized(true)
        }
        const errors: string[] = response.errors
        errors.forEach(e => toast.error(e))
      }
      setLoaded(true)
    }
    void fetchData()
  }, [refreshTime])

  const hideInterestFormDetails = async (): Promise<void> => {
    const val: intakeVisiblityUpdate = {
      childId: child.id,
      visible: false
    }
    const { success } = await sendPost('/ChildDetails/ToggleInterestFormDetailsDisplay', val)
    if (success && child.interestFormDetails != null) { setChild({ ...child, interestFormDetails: { ...child.interestFormDetails, visible: false } }) }
  }

  if (!loaded) return <CircularProgress role='loading' />
  if (unauthorized) return <NotAllowed />

  function openChildEdit (): void {
    const editChild = generatePath(AppRoutes.CARENETWORK_MANAGEMENT_EDIT, { id: props.childID.toString() })
    nav(editChild)
  }

  function handleSubmit (childData: ChildEditValues, childImage: File | null): void {
    const updateUser = async (): Promise<void> => {
      if (!isRequestLoading) {
        setIsRequestLoading(true)

        const { response } = await sendPost('/ChildDetails/Update', childData)
        if (response.success === false) {
          response.errors.forEach((e: postError) => toast.error(e.error))
        } else {
          const fileData = new FormData()
          fileData.append('id', childData.id.toString())
          if (childImage !== null) {
            if (childImage !== null) { fileData.append('file', childImage) }
            await sendPostFormData('/ChildDetails/UploadImage', fileData)
          } else {
            await sendPostFormData('/ChildDetails/RemoveImage', fileData)
          }

          setOpen(false)
          setRefreshTime()
        }
        setIsRequestLoading(false)
      }
    }
    void updateUser()
  }

  return <>
    <Box sx={{ width: '100%;' }}>
      <ErrorProvider>
        <Grid container spacing={2} sx={{ m: 'auto' }}>
          <Grid item xs={12} sm={6} lg={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ChildInfoCard id={child.id} onEdit={openChildEdit} />
              </Grid>
              <Grid item xs={12}>
                <LocationCardList
                  childID={child?.id ?? 0}
                  requestingModule={ChildAuditAccessTypeEnum.CareNetwork}
                  onDataChange={() => { setRefreshTime() }}
                />
              </Grid>
              {userPermissions.CareNetwork_Caregiver && <Grid item xs={12}>
                <CareNetworkDetails childId={child.id} redirectText="Child Details" />
              </Grid>
              }
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Grid container spacing={2}>
              {showCareManagement && <Grid item xs={12}>
                <CareManagementSummary childID={child.id} childName={child.firstName + ' ' + child.lastName} variant='card' />
              </Grid>}
              {userPermissions.CareNetwork_SensitiveDetails && <Grid item xs={12}>
                <IncomeCard childID={child.id} readOnly={!userPermissions.CareNetwork_SensitiveDetails_Edit} />
              </Grid>
              }
              {(child.interestFormDetails != null) && <Grid item xs={12}>
                <Card sx={{ display: 'flex', flexDirection: 'column', borderRadius: '32px', cursor: 'default', p: '24px' }}>
                  <>
                    <Typography variant='h5' fontWeight={'bold'}>Interest Form Details</Typography>
                    <InterestFormCard
                      onClick={hideInterestFormDetails}
                      notes={child.interestFormDetails?.notes}
                      services={child.interestFormDetails?.interestedServices}
                    />
                  </>
                </Card>
              </Grid>}
            </Grid>
          </Grid>
        </Grid>
      </ErrorProvider>
    </Box>

    {open && (
      <ChildEdit
        isSibling={false}
        isUnborn={isUnborn}
        child={child as ChildSiblings}
        onClose={() => { setOpen(false) }}
        onSubmit={handleSubmit}
        open={open}
      />
    )
    }

    {showConfirmMerge &&
      <ChildMerge
        open={showConfirmMerge}
        child={child}
        onClose={(deleted: boolean) => {
          if (deleted) {
            nav('/CareNetworkManagement')
          } else {
            setShowConfirmMerge(false)
          }
        }}
      />
    }
  </>
}
